import React from 'react';
import { Building2, CheckCircle, Calendar, Users, FileText, Award } from 'lucide-react';

const ScopeOfWork = () => {
  const printDocument = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Print Button - Hidden in print */}
      <div className="no-print fixed top-4 right-4 z-50">
        <button
          onClick={printDocument}
          className="bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-all duration-200 shadow-lg"
          data-testid="print-scope-button"
        >
          Download / Print PDF
        </button>
      </div>

      {/* Document Content */}
      <div className="max-w-5xl mx-auto px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12 pb-8 border-b-4 border-black">
          <div className="flex items-center justify-center mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_builder-solutions-1/artifacts/ph04hc4l_logo-black.png" 
              alt="NOVA HOMES" 
              className="h-24 w-auto"
            />
          </div>
          <p className="text-xl text-gray-600 mb-4">Real Estate Mandate & Construction Solutions</p>
          <p className="text-gray-500">Office no 2, Silverwoods, Tapkir Chowk, Rahatani, Pune, Maharashtra</p>
          <p className="text-gray-500">Info@novahomes.in | +91 74989 63176</p>
        </div>

        {/* Document Title */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">SCOPE OF WORK</h2>
          <p className="text-lg text-gray-600">Comprehensive Real Estate & Construction Services</p>
        </div>

        {/* Introduction */}
        <section className="mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
            <Building2 className="w-6 h-6 text-gray-900" />
            <span>1. INTRODUCTION</span>
          </h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            NOVA HOMES is a premier real estate mandate company specializing in assured inventory clearance and comprehensive construction services. We guarantee builders timely sales within stipulated periods while providing end-to-end construction planning and execution support.
          </p>
          <p className="text-gray-700 leading-relaxed">
            This document outlines the scope of services provided by NOVA HOMES for real estate development projects, including mandate services, feasibility studies, design planning, and construction support.
          </p>
        </section>

        {/* Services Offered */}
        <section className="mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center space-x-2">
            <FileText className="w-6 h-6 text-gray-900" />
            <span>2. SERVICES OFFERED</span>
          </h3>

          {/* Service 2.1 */}
          <div className="mb-6">
            <h4 className="text-xl font-bold text-gray-800 mb-3">2.1 Real Estate Mandate Services</h4>
            <p className="text-gray-700 mb-3">
              Complete sales and marketing mandate with guaranteed inventory clearance within agreed timelines.
            </p>
            <div className="bg-gray-100 rounded-lg p-4 space-y-2">
              {[
                'Comprehensive market analysis and pricing strategy',
                'Multi-channel marketing and promotion campaigns',
                'Buyer identification and qualification',
                'Sales negotiation and closure support',
                'Timeline-bound performance guarantees',
                'Regular progress reporting and analytics'
              ].map((item, index) => (
                <div key={index} className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-gray-900 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Service 2.2 */}
          <div className="mb-6">
            <h4 className="text-xl font-bold text-gray-800 mb-3">2.2 Plot Feasibility Analysis</h4>
            <p className="text-gray-700 mb-3">
              Detailed feasibility studies to determine development viability and optimize project planning.
            </p>
            <div className="bg-gray-100 rounded-lg p-4 space-y-2">
              {[
                'Site analysis and topographical survey',
                'Zoning and regulatory compliance review',
                'Market demand assessment',
                'Financial viability and ROI projections',
                'Risk identification and mitigation strategies',
                'Highest and best use recommendations'
              ].map((item, index) => (
                <div key={index} className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-gray-900 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Service 2.3 */}
          <div className="mb-6">
            <h4 className="text-xl font-bold text-gray-800 mb-3">2.3 Landscape Design Services</h4>
            <p className="text-gray-700 mb-3">
              Professional landscape architecture creating functional and aesthetically pleasing outdoor spaces.
            </p>
            <div className="bg-gray-100 rounded-lg p-4 space-y-2">
              {[
                'Conceptual landscape master planning',
                'Detailed landscape design drawings',
                '3D visualization and renderings',
                'Plant selection and specifications',
                'Hardscape and amenity design',
                'Sustainable landscaping solutions',
                'Implementation support and contractor coordination'
              ].map((item, index) => (
                <div key={index} className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-gray-900 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Service 2.4 */}
          <div className="mb-6">
            <h4 className="text-xl font-bold text-gray-800 mb-3">2.4 Elevation Layout & Design</h4>
            <p className="text-gray-700 mb-3">
              Architectural elevation designs combining modern aesthetics with structural efficiency.
            </p>
            <div className="bg-gray-100 rounded-lg p-4 space-y-2">
              {[
                'Contemporary elevation design concepts',
                'Detailed facade drawings and specifications',
                'Material selection and finishes',
                'Structural integration planning',
                'Compliance with local building codes',
                'Multiple design options and revisions',
                'Construction-ready working drawings'
              ].map((item, index) => (
                <div key={index} className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-gray-900 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Service 2.5 */}
          <div className="mb-6">
            <h4 className="text-xl font-bold text-gray-800 mb-3">2.5 Builder Floor Plans</h4>
            <p className="text-gray-700 mb-3">
              Optimized floor plan designs maximizing space utilization and market appeal.
            </p>
            <div className="bg-gray-100 rounded-lg p-4 space-y-2">
              {[
                'Space planning and optimization',
                'Multiple unit configuration options',
                'Vastu-compliant layouts (if required)',
                'Detailed floor plan drawings',
                'Furniture layout suggestions',
                'Regulatory approval support',
                'Variations for different buyer segments'
              ].map((item, index) => (
                <div key={index} className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-gray-900 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Project Timeline */}
        <section className="mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center space-x-2">
            <Calendar className="w-6 h-6 text-gray-900" />
            <span>3. PROJECT TIMELINE</span>
          </h3>
          <div className="bg-gray-50 rounded-lg p-6">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-300">
                  <th className="text-left py-3 px-4 font-bold text-gray-900">Phase</th>
                  <th className="text-left py-3 px-4 font-bold text-gray-900">Activities</th>
                  <th className="text-left py-3 px-4 font-bold text-gray-900">Duration</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200">
                  <td className="py-3 px-4 font-semibold text-gray-800">Phase 1</td>
                  <td className="py-3 px-4 text-gray-700">Initial consultation, site survey, feasibility analysis</td>
                  <td className="py-3 px-4 text-gray-700">2-3 weeks</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-3 px-4 font-semibold text-gray-800">Phase 2</td>
                  <td className="py-3 px-4 text-gray-700">Concept development, design proposals, client review</td>
                  <td className="py-3 px-4 text-gray-700">3-4 weeks</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-3 px-4 font-semibold text-gray-800">Phase 3</td>
                  <td className="py-3 px-4 text-gray-700">Detailed design, working drawings, specifications</td>
                  <td className="py-3 px-4 text-gray-700">4-6 weeks</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-3 px-4 font-semibold text-gray-800">Phase 4</td>
                  <td className="py-3 px-4 text-gray-700">Approvals, documentation, permits processing</td>
                  <td className="py-3 px-4 text-gray-700">4-8 weeks</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-semibold text-gray-800">Phase 5</td>
                  <td className="py-3 px-4 text-gray-700">Execution support, sales mandate activation</td>
                  <td className="py-3 px-4 text-gray-700">Ongoing</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Deliverables */}
        <section className="mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center space-x-2">
            <Award className="w-6 h-6 text-gray-900" />
            <span>4. DELIVERABLES</span>
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              'Comprehensive feasibility report',
              'Architectural floor plans (all floors)',
              'Elevation designs (all facades)',
              'Landscape master plan and details',
              '3D visualizations and renderings',
              'Detailed working drawings',
              'Material specifications',
              'Structural planning inputs',
              'BOQ (Bill of Quantities)',
              'Regulatory approval drawings',
              'Marketing collateral support',
              'Sales strategy and implementation plan'
            ].map((item, index) => (
              <div key={index} className="flex items-center space-x-3 bg-gray-100 p-3 rounded-lg">
                <CheckCircle className="w-5 h-5 text-gray-900 flex-shrink-0" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Terms and Conditions */}
        <section className="mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center space-x-2">
            <Users className="w-6 h-6 text-gray-900" />
            <span>5. TERMS & CONDITIONS</span>
          </h3>
          <div className="space-y-4 text-gray-700">
            <div>
              <h4 className="font-bold text-gray-800 mb-2">5.1 Payment Terms</h4>
              <p>Payment schedule will be milestone-based with specific deliverables tied to each installment. Standard terms include 30% advance, 40% on design approval, and 30% on final delivery.</p>
            </div>
            <div>
              <h4 className="font-bold text-gray-800 mb-2">5.2 Scope Changes</h4>
              <p>Any changes to the agreed scope of work will be documented through written change orders with associated cost and timeline adjustments.</p>
            </div>
            <div>
              <h4 className="font-bold text-gray-800 mb-2">5.3 Client Responsibilities</h4>
              <p>Client shall provide timely approvals, site access, and all required documentation. Delays due to client-side factors may result in timeline extensions.</p>
            </div>
            <div>
              <h4 className="font-bold text-gray-800 mb-2">5.4 Warranty</h4>
              <p>NOVA HOMES provides warranty on all design work for professional errors. Modifications by third parties void this warranty.</p>
            </div>
            <div>
              <h4 className="font-bold text-gray-800 mb-2">5.5 Intellectual Property</h4>
              <p>All designs and documents remain property of NOVA HOMES until full payment is received. Upon payment, client receives license for the specific project use.</p>
            </div>
          </div>
        </section>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t-2 border-gray-300 text-center">
          <p className="text-gray-600 mb-4">
            For detailed quotations and project-specific scope of work, please contact us:
          </p>
          <div className="space-y-2 text-gray-700">
            <p className="font-semibold">NOVA HOMES</p>
            <p>Email: Info@novahomes.in</p>
            <p>Phone: +91 74989 63176</p>
            <p>Office: Office no 2, Silverwoods, Tapkir Chowk, Rahatani, Pune</p>
          </div>
          <p className="text-sm text-gray-500 mt-6">
            © {new Date().getFullYear()} NOVA HOMES. All rights reserved.
          </p>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          @page {
            margin: 1cm;
          }
        }
      `}</style>
    </div>
  );
};

export default ScopeOfWork;